import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { useSelector } from "react-redux";

import BookingClientInformation from "./Components/steps/ClientInformation";
import BookingPreviewSidebar from "./Components/PreviewSidebar";
import CustomStepper from "./Components/CustomStepper";
import DeceasedInformation from "./Components/steps/DeceasedInfo";
import ConferenceServiceStep from "./Components/steps/ConferenceServiceInformation";
import FuneralServiceStep from "./Components/steps/FuneralServiceInformation";
import PackageStep from "./Components/steps/PackageSelection";
import ConferenceStreamingStep from "./Components/steps/ConferenceStreamingStep";
import ChurchServiceStep from "./Components/steps/ChurchServiceStep";
import EventServiceStep from "./Components/steps/EventServiceStep";
import OtherServiceStep from "./Components/steps/OtherServiceStep";

import { BookingData } from "../../../types/bookings";
import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";
import { checkPermission } from "../../../service/permissionGuard";
import { PERMISSIONS } from "../../../constants/permissions";
import { buildEditBookingFormData } from "../utils/build-edit-booking-form-data";
import type { BookingResponse } from "../types/edit-booking";
import { type BookingType, type StepComponentProps } from "./AddBookingV2";
import { initialValues } from "../AddBooking";
import BookingLayout from "./Components/AddBookingLayout.tsx";
import ServicesStep from "./Components/steps/ServiceStep";

const BOOKING_STEPS: Record<
  BookingType,
  { label: string; component: React.ComponentType<StepComponentProps> }[]
> = {
  funeral: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Deceased",
      component: DeceasedInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: FuneralServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
          label: "Services",
          component: ServicesStep as React.ComponentType<StepComponentProps>,
        }, 
  ],
  funeral_home: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Deceased",
      component: DeceasedInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: FuneralServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    }, 
  ],
  church: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: ChurchServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    }, 
  ],
  event: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: EventServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    }, 
  ],
  other: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component: OtherServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    }, 
  ],
  conference: [
    {
      label: "Client",
      component:
        BookingClientInformation as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Service",
      component:
        ConferenceServiceStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Streaming",
      component:
        ConferenceStreamingStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Package",
      component: PackageStep as React.ComponentType<StepComponentProps>,
    },
    {
      label: "Services",
      component: ServicesStep as React.ComponentType<StepComponentProps>,
    }, 
  ],
};

const getBookingType = (raw: string): BookingType => {
  const map: Record<string, BookingType> = {
    CONFERENCE: "conference",
    FUNERAL: "funeral",
    FUNERAL_HOME: "funeral_home",
    CHURCH: "church",
    EVENT: "event",
    OTHER: "other",
  };
  return map[raw?.toUpperCase()] ?? "other";
};

const mapBookingResponseToData = (booking: BookingResponse): BookingData => {
  const service = booking.services?.[0];
  const event = booking.events?.[0];
  const deceased = booking.deceased?.[0];
  const isConferenceOrEvent =
    booking.booking_type === "CONFERENCE" || booking.booking_type === "OTHER";
  const isFuneralHome = booking.booking_type?.toUpperCase() === "FUNERAL_HOME";

  return {
    ...initialValues,
    booking_type: booking.booking_type,
    status: booking.status,
    payment_method: booking.payment_method,
    total: parseFloat(booking.total) || 0,
    source_of_booking: booking.source_of_booking || "",
    contact_id: booking.contact?.id ?? null,
    contacts: booking.contacts?.length
      ? booking.contacts.map((c) => ({
          contact_id: c.contact.id,
          role: c.role,
          contact: {
            id: c.contact.id,
            first_name: c.contact.first_name ?? null,
            last_name: c.contact.last_name ?? null,
            name: c.contact.name ?? null,
            email: c.contact.email ?? null,
            phone: c.contact.phone ?? null,
          },
        }))
      : booking.contact
        ? [
            {
              contact_id: booking.contact.id,
              role: "primary",
              contact: {
                id: booking.contact.id,
                first_name: booking.contact.first_name ?? null,
                last_name: booking.contact.last_name ?? null,
                name: booking.contact.name ?? null,
                email: booking.contact.email ?? null,
                phone: booking.contact.phone ?? null,
              },
            },
          ]
        : [],
    first_name: booking.contact?.first_name || "",
    surname: booking.contact?.last_name || "",
    email: booking.contact?.email || "",
    phone: booking.contact?.phone || "",
    company_id: booking.company?.id ?? null,
    client_type: isFuneralHome ? "funeral_home" : "standard",
    funeral_home_id: isFuneralHome ? (booking.funeral_home?.id ?? null) : null,
    company_name:
      (isFuneralHome ? booking.funeral_home?.name : booking.company?.name) ||
      "",
    company_contact_id: booking.company_contact?.id,
    deceased_name: deceased?.first_name || "",
    deceased_surname: deceased?.surname || "",
    deceased_salutation: deceased?.salutation || "",
    second_name: deceased?.second_name || "",
    deceased_nickname: deceased?.nickname || "",
    gender: deceased?.gender || "",
    deceased_dob: deceased?.date_of_birth || "",
    deceased_dod: deceased?.date_of_death || "",
    religion: deceased?.religion || "",
    denomination: deceased?.denomination || "",
    relationship_to_deceased: deceased?.relationship_to_deceased || "",
    photo_url: deceased?.person_image || "",
    deceased_id: deceased?.id,
    date_of_service: service?.date_of_service || "",
    start_time: isConferenceOrEvent
      ? event?.start_time?.slice(0, 5) || ""
      : service?.start_time?.slice(0, 5) || "",
    end_time: isConferenceOrEvent
      ? event?.end_time?.slice(0, 5) || ""
      : service?.end_time?.slice(0, 5) || "",
    church_name: service?.church_name || "",
    type_of_service: service?.type_of_service || "",
    preacher_name: service?.preacher_name || "",
    preacher_surname: service?.preacher_surname || "",
    leader_preacher_name: service?.leader_preacher_name || "",
    leader_preacher_surname: service?.leader_preacher_surname || "",
    mc_name: service?.mc_name || "",
    eulogist_name: service?.eulogist_name || "",
    special_request: service?.special_request || event?.special_request || "",
    service_notes: service?.service_notes || event?.service_notes || "",
    notes: service?.service_notes || event?.service_notes || "",
    service_id: service?.id,
    venue_ids: isConferenceOrEvent
      ? booking.events?.map((e) => e.venue?.id?.toString()).filter(Boolean) ||
        []
      : booking.services?.map((s) => s.venue?.id?.toString()).filter(Boolean) ||
        [],
    venue_name: service?.venue?.name || event?.venue?.name || "",
    address_one: service?.venue?.address_one || event?.venue?.address_one || "",
    address_two: service?.venue?.address_two || event?.venue?.address_two || "",
    suburb: service?.venue?.suburb || event?.venue?.suburb || "",
    city: service?.venue?.city || event?.venue?.city || "",
    areAddressFieldsDisabled: !!(service?.venue?.id || event?.venue?.id),
    title: event?.title || booking.title || "",
    hosts: event?.hosts || "",
    host_surname: event?.host_surname || "",
    guests: event?.guests || "",
    guest_surname: event?.guest_surname || "",
    conference_type: booking.conference_type || event?.conference_type || "",
    start_date_of_event: event?.start_date_of_event || "",
    end_date_of_event: event?.end_date_of_event || "",
    expected_audience_size: event?.expected_audience_size ?? null,
    access_type: event?.access_type || "",
    enable_livestreaming: event?.enable_livestreaming ?? false,
    streaming_platform: event?.streaming_platform || "",
    session_to_stream: event?.session_to_stream || "",
    specific_sessions: event?.specific_sessions || "",
    record_stream: event?.record_stream ?? false,
    provide_on_demand_access: event?.provide_on_demand_access ?? false,
    retention_duration: event?.retention_duration || "",
    require_custom_branding: event?.require_custom_branding ?? false,
    branding_requirements: event?.branding_requirements || "",
    event_id: event?.id,
    package: booking.package_item
      ? {
          id: booking.package_item.package,
          name: booking.package_item.name,
          price: parseFloat(booking.package_item.price) || 0,
          contents: [booking.package_item.description],
        }
      : undefined,
    package_item: booking.package_item
      ? {
          id: booking.package_item.id,
          name: booking.package_item.name,
          price: parseFloat(booking.package_item.price),
          package: booking.package_item.package,
          description: booking.package_item.description,
        }
      : undefined,
    "Audio Visual Equipment":
      booking.package_item?.services
        ?.filter((s) => s.service_category === "Audio Visual Equipment")
        .map((s) => s.id?.toString()) || [],
    Screens:
      booking.package_item?.services
        ?.filter((s) => s.service_category === "Screens")
        .map((s) => s.id?.toString()) || [],
    Design:
      booking.package_item?.services
        ?.filter((s) => s.service_category === "Design")
        .map((s) => s.id?.toString()) || [],
    "Visual Presentation & Audio":
      booking.package_item?.services
        ?.filter((s) => s.service_category === "Visual Presentation & Audio")
        .map((s) => s.id?.toString()) || [],
  };
};

const EditBookingV2: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState<BookingData>({
    contact_id: null,
  });
  const [bookingType, setBookingType] = useState<BookingType>("funeral");
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchBooking = async () => {
      try {
        const res = await apiService.get<BookingResponse>(
          `/api/bookings/booking/${id}/`,
        );
        setBookingData(mapBookingResponseToData(res.data));
        setBookingType(getBookingType(res.data.booking_type));
      } catch {
        toast.error("Failed to load booking.");
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  const steps = BOOKING_STEPS[bookingType];
  const ActiveComponent = steps[activeStep]?.component;
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps.length - 1;

  const handleUpdate = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      checkPermission(PERMISSIONS.BOOKING_CHANGE);
      const formData = buildEditBookingFormData(bookingData, Number(id));
      await apiService.put(
        "/api/bookings/booking/update-fullbooking/",
        formData,
      );
      toast.success("Booking updated successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      setTimeout(() => navigate(`/bookings/${id}`), 2000);
    } catch (err: unknown) {
      const errorResponse = (
        err as { response?: { data?: Record<string, unknown> } }
      )?.response;
      const errorMessage =
        (errorResponse?.data as { detail?: string })?.detail ||
        Object.values(errorResponse?.data || {})
          .flat()
          .join(" ") ||
        "Failed to update booking.";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (isLastStep) handleUpdate();
    else setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress sx={{ color: colors.primary[400] }} />
      </Box>
    );
  }

  return (
    <BookingLayout
      onNext={handleNext}
      onBack={handleBack}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      submitting={submitting}
      isEdit={true}
      onUpdate={handleUpdate}
      stepperComponent={
        <CustomStepper
          steps={steps.map((s) => s.label)}
          activeStep={activeStep}
          onStepClick={setActiveStep}
        />
      }
      formContentComponent={
        ActiveComponent ? (
          <ActiveComponent
            bookingData={bookingData}
            setBookingData={setBookingData}
            onNext={handleNext}
            onBack={handleBack}
            bookingType={bookingType}
            submitting={submitting}
            isEdit={true}
            onUpdate={handleUpdate}
          />
        ) : null
      }
      sidebarContentComponent={
        <BookingPreviewSidebar
          bookingData={bookingData}
          bookingType={bookingType}
        />
      }
    />
  );
};

export default EditBookingV2;
